import TestingSetupActions from '../components/TestingSetupActions'
import OverviewTestingSummary from '../components/OverviewTestingSummary'
import { useGetBugBreakdownQuery, useGetBugCountsQuery, useGetProjectTestSessionsInfiniteQuery } from '../hooks/bug-management/useProjectFindings'
import SiteMapTab from '../components/SiteMapTab'
import { useExperimentalFlag } from '../hooks/useExperimentalFlag'
import { useCallback, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, Navigate } from '../components/nav'
import { RefreshCw, LogIn, Route as RouteIcon, Smartphone, ChevronRight, Bug, CheckCircle } from 'lucide-react'
import type { RootState, AppDispatch } from '../store'
import {


  useGetProjectBugsQuery,
  useGetProjectExplorationsQuery,
  useGetProjectJourneysInfiniteQuery,
  useGetProjectQuery,

  useGetProcessingSummaryQuery,
  PROJECT_LIST_PAGE_SIZE,
} from '../services/projectDataApi'
import OverviewExplorations from '../components/OverviewExplorations'
import TabNavigation from '../components/TabNavigation'
import { usePlaywrightTestsEnabled } from '../hooks/usePlaywrightTestsEnabled'
import { projectTabs } from '../components/projectTabs'
import JourneyLightbox from '../components/JourneyLightbox'
import BugListTab from '../components/bug-management/BugListTab'
import TestRunListTab from '../components/TestRunListTab'
import PlaywrightTestsTab from '../components/PlaywrightTestsTab'
import SecurityTab from '../components/SecurityTab'
import { PlaywrightInfoCard, UnresolvedBugsHeader, OverviewBugTable, OverviewTestRuns } from '../components/OverviewSummary'
import { JourneyStatusIcon } from '../components/JourneysTab'
import type { Journey } from '../data/projectTypes'
import { isBugResolvedForDisplay, isSurfacedFinding } from '../../netlify/functions/lib/bug-management/statuses'
import { login, postLoginReturnTo } from '../components/UserMenu'
import { useSignInOnUnauthorized } from '../hooks/useSignInOnUnauthorized'
import { isProjectAccessDeniedError, projectQueryErrorMessage } from '../lib/projectQueryError'
import { projectPause, type ProjectPause } from '../lib/projectPause'
import { useInitialTestRun } from '../hooks/useInitialTestRun'
import InitialTestRunView from '../components/InitialTestRunView'
import { displayRunStatus, isPendingJourneyRun } from '../guidance/journey/run-status'
import './ProjectOverview.css'

import { lazyWithRetry } from '../lib/lazyWithRetry'
import ChunkErrorBoundary from '../components/ChunkErrorBoundary'

// The donut is the only recharts consumer reachable from the eagerly-executed entry bundle (App →
// here). Loading it as a deferred chunk keeps the recharts library out of the entry bundle (QA bug
// bug-mv5s0n2s-i8jh, issue #5955); the Suspense fallback reuses the same donut skeleton the
// data-loading state shows.
const BugBreakdownChart = lazyWithRetry(() => import('../components/BugBreakdownChart'))

// One journey rendered as a line item in the overview list. Clicking opens the journey lightbox
// (inline replay + filed bugs), matching the previous grid behavior. The trailing icon surfaces the
// latest run result: a bug (red) / resolved bug (green) when the last run filed one, otherwise the
// run status (completed / failed / in-progress / queued), or the project's pause when that "in
// progress" last run is really a run the pause stopped.
function OverviewJourneyRow({
  journey,
  onOpen,
  pause,
}: {
  journey: Journey
  onOpen: (id: string) => void
  pause: ProjectPause | null
}) {
  const isActive = isPendingJourneyRun(journey.last_run_status)
  const bugId = journey.last_run_bug_id
  const showBug = !isActive && bugId && isSurfacedFinding(journey.last_run_bug_status)
  const bugResolved = isBugResolvedForDisplay(journey.last_run_bug_status)

  return (
    <li className="project-overview__journey-row">
      <button
        type="button"
        className="project-overview__journey-row-btn"
        onClick={() => onOpen(journey.id)}
        data-testid={`overview-journey-row-${journey.id}`}
        data-analytics-event="project-overview-journey-row"
      >
        <ChevronRight size={16} className="project-overview__journey-row-chevron" aria-hidden />
        <RouteIcon size={16} className="project-overview__journey-row-icon" aria-hidden />
        <span className="project-overview__journey-row-name">{journey.name}</span>
        {journey.mobile && (
          <span className="project-overview__journey-row-flag" title="Mobile viewport (375×812)">
            <Smartphone size={12} aria-hidden /> Mobile
          </span>
        )}
        <span className="project-overview__journey-row-status">
          {showBug ? (
            <span
              className={`project-overview__journey-row-bug${bugResolved ? ' project-overview__journey-row-bug--resolved' : ''}`}
              title={bugResolved ? 'Bug resolved' : 'Bug found'}
            >
              {bugResolved ? <CheckCircle size={16} aria-hidden /> : <Bug size={16} aria-hidden />}
            </span>
          ) : (
            <JourneyStatusIcon
              status={isActive ? (journey.last_run_status === 'queued' ? 'queued' : 'in-progress') : displayRunStatus(journey.last_run_status ?? null, journey.last_run_outcome_status)}
              pause={pause}
            />
          )}
        </span>
      </button>
    </li>
  )
}

function OverviewBugsSkeleton({ chart = false }: { chart?: boolean }) {
  return (
    <div
      className={chart ? 'project-overview__bugs-chart-skeleton' : 'project-overview__bugs-table-skeleton'}
      role="status"
      aria-label={chart ? 'Loading bug breakdown' : 'Loading bugs'}
    >
      {chart ? <span className="project-overview__skeleton-donut" /> : Array.from({ length: 4 }).map((_, i) => (
        <span key={i} className="project-overview__skeleton-line project-overview__skeleton-line--row" />
      ))}
    </div>
  )
}

function OverviewRunsSkeleton() {
  return (
    <section className="project-overview__runs-skeleton" role="status" aria-label="Loading test runs" data-testid="overview-test-runs-loading">
      <span className="project-overview__skeleton-line project-overview__skeleton-line--title" />
      {Array.from({ length: 3 }).map((_, i) => (
        <span key={i} className="project-overview__skeleton-line project-overview__skeleton-line--row" />
      ))}
    </section>
  )
}

// The project overview page — the default overview for every project in the full app (see App.tsx;
// the embedded iframe keeps the classic tabbed ProjectDetail). This surface mirrors the classic
// overview's content — a live QA-activity banner, then (when there are open bugs) the top open bugs +
// bug breakdown shown above the exploration replay just like the classic overview, the active
// exploration replay (cycle through all explorations), and journey cards — as a single scroll, since
// navigation/QA chat live in the app sidebar and top bar rather than a tab bar. The project name /
// URL and the Share / Settings / Admin controls live in the AppShell chat top bar, so this page only
// owns the project tabs and their incrementally loaded content.

export default function ProjectOverview() {
  const { projectId, tab } = useParams<{ projectId: string; tab: string }>()
  const { enabled: siteMapEnabled, loaded: siteMapLoaded } = useExperimentalFlag('site_map')
  const user = useSelector((state: RootState) => state.auth.user)
  const activeTab = tab === 'site-map' ? 'site-map' : tab === 'playwright-tests' ? 'playwright-tests' : tab === 'bugs' ? 'bugs' : tab === 'test-runs' ? 'test-runs' : tab === 'security' ? 'security' : 'overview'
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const [livePollingInterval, setLivePollingInterval] = useState(60_000)

  const projectQuery = useGetProjectQuery(projectId ?? '', { skip: !projectId })
  const { enabled: playwrightEnabled, loaded: playwrightLoaded } = usePlaywrightTestsEnabled(projectId ?? '')
  const project = projectQuery.data ?? null
  const loading = projectQuery.isLoading
  // Shared with ProjectDetail so a 401 reads "Login required …" here too — the Sign In button in the
  // error state below is gated on that wording, so a hardcoded message hid it from signed-out users.
  const error = projectQueryErrorMessage(projectQuery.error, Boolean(user))
  // A deterministic 403 "Access denied" never clears on refetch — a Retry button for it would just
  // re-fire the identical request and get the identical denial — so the error state below withholds
  // Retry for that case.
  const accessDenied = isProjectAccessDeniedError(projectQuery.error)
  // A private project opened while signed out goes to sign-in and comes back here, rather than
  // dead-ending on the error state below.
  const redirectingToLogin = useSignInOnUnauthorized(projectQuery.error)
  const explorationsQuery = useGetProjectExplorationsQuery(
    { projectId: projectId ?? '', page: 1, pageSize: 100 },
    { skip: !projectId, pollingInterval: 60_000, skipPollingIfUnfocused: true },
  )
  const journeyListQuery = useGetProjectJourneysInfiniteQuery(
    { projectId: projectId ?? '', parentId: null, pageSize: PROJECT_LIST_PAGE_SIZE },
    { skip: !projectId, pollingInterval: livePollingInterval, skipPollingIfUnfocused: true, refetchCachedPages: false },
  )
  const testSessionsQuery = useGetProjectTestSessionsInfiniteQuery(
    { projectId: projectId ?? '', pageSize: PROJECT_LIST_PAGE_SIZE },
    { skip: !projectId, pollingInterval: livePollingInterval, skipPollingIfUnfocused: true, refetchCachedPages: false },
  )
  const bugCountsQuery = useGetBugCountsQuery(projectId ?? '', { skip: !projectId })
  const processingQuery = useGetProcessingSummaryQuery(projectId ?? '', { skip: !projectId, pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const overviewBugsQuery = useGetProjectBugsQuery(
    { projectId: projectId ?? '', page: 1, pageSize: 20, hideResolved: true, severitySort: 'desc' },
    { skip: !projectId || activeTab !== 'overview', pollingInterval: 60_000, skipPollingIfUnfocused: true },
  )
  const bugBreakdownQuery = useGetBugBreakdownQuery(projectId ?? '', { skip: !projectId || activeTab !== 'overview' })
  const journeyListPages = journeyListQuery.data?.pages ?? []
  const loadedJourneys = useMemo(() => {
    const seen = new Set<string>()
    return journeyListPages.flatMap(page => page.items).filter(journey => {
      if (seen.has(journey.id)) return false
      seen.add(journey.id)
      return true
    })
  }, [journeyListPages])
  const journeys = loadedJourneys
  const testRunPages = testSessionsQuery.data?.pages ?? []
  const testRuns = useMemo(() => {
    const seen = new Set<string>()
    return testRunPages.flatMap(page => page.items).filter(session => {
      if (seen.has(session.id)) return false
      seen.add(session.id)
      return true
    })
  }, [testRunPages])
  const explorations = explorationsQuery.data?.items ?? []
  const journeyStatusCounts = journeyListPages[0]?.statusCounts ?? {}
  const journeyTotal = journeyListPages[0]?.totalAll ?? journeyListPages[0]?.total ?? journeys.length
  const journeyDataLoading = journeyListQuery.isLoading
  const bugCounts = bugCountsQuery.data
  const bugOpen = bugCounts?.openCount ?? 0
  const overviewBugs = overviewBugsQuery.data?.items ?? []
  const bugCountPending = bugCountsQuery.isLoading
  const overviewBugsPending = overviewBugsQuery.isLoading
  const bugBreakdownPending = bugBreakdownQuery.isLoading
  const testRunsLoading = testSessionsQuery.isLoading
  const hasLiveJourneyState = Number(journeyStatusCounts.queued ?? 0) > 0
    || Number(journeyStatusCounts['in-progress'] ?? 0) > 0
    || testRuns.some(run => run.status === 'in-progress')
  useEffect(() => {
    setLivePollingInterval(hasLiveJourneyState ? 5_000 : 60_000)
  }, [hasLiveJourneyState])
  const initialTestRun = useInitialTestRun(projectId, {
    testSessions: testRuns,
    testSessionsProjectId: projectId,
    testSessionsLoading: testSessionsQuery.isLoading,
    explorations,
    expTotal: explorationsQuery.data?.total ?? 0,
    explorationsProjectId: projectId,
    explorationsFetchPending: explorationsQuery.isLoading || explorationsQuery.isFetching,
    journeys,
    journeyTotal,
    journeysProjectId: projectId,
    journeysFetchPending: journeyListQuery.isLoading || journeyListQuery.isFetching,
    journeyStatusCounts,
    bugTotalCount: bugCounts?.total ?? 0,
    bugsProjectId: projectId,
    bugCountPending: bugCountsQuery.isLoading || bugCountsQuery.isFetching,
    running: processingQuery.data?.running ?? [],
    project: project ?? undefined,
  })
  const showInitialTestRun = initialTestRun.showInterim && activeTab !== 'playwright-tests' && activeTab !== 'site-map'

  // The journey whose player + filed-bug list is open in the lightbox modal (null = none). Tracked by
  // id (not grid index) so background polling reordering/refreshing the list keeps the right journey
  // selected. `lightboxIndex` resolves it to a position for prev/next navigation.
  const [lightboxJourneyId, setLightboxJourneyId] = useState<string | null>(null)
  // Overview preview: the 5 most recent journeys, but surface the ones with an unresolved (open) bug
  // first so problems lead. This preview only appears before the project has a Test Run; afterward,
  // journeys are presented within the run that owns them.
  const recentJourneys = useMemo(() => {
    const hasOpenBug = (j: Journey) =>
      !!j.last_run_bug_id &&
      isSurfacedFinding(j.last_run_bug_status) &&
      !isBugResolvedForDisplay(j.last_run_bug_status)
    const recency = (j: Journey) => (j.last_run_at ? new Date(j.last_run_at).getTime() : 0)
    return [...journeys]
      .sort((a, b) => {
        const bugDelta = (hasOpenBug(a) ? 0 : 1) - (hasOpenBug(b) ? 0 : 1)
        if (bugDelta !== 0) return bugDelta
        return recency(b) - recency(a)
      })
      .slice(0, 5)
  }, [journeys])
  const lightboxIndex = lightboxJourneyId ? journeys.findIndex(j => j.id === lightboxJourneyId) : -1
  // The chat sidebar's test-run entries deep-link a journey into this page via `?journey=<id>` so it
  // opens in the lightbox modal (in the main pane) rather than the standalone Journey detail page.
  const [searchParams, setSearchParams] = useSearchParams()
  const lightboxParam = searchParams.get('journey')

  // Open the lightbox for the `?journey=` deep-link. Keyed only on the param — not on `journeys` — so a
  // background poll refreshing the list never re-fires this and yanks the selection back after the user
  // has paged to another journey with prev/next. A fresh link (clicking another journey in the chat)
  // changes the param and re-opens, even after the previous one was closed.
  useEffect(() => {
    if (lightboxParam) setLightboxJourneyId(lightboxParam)
  }, [lightboxParam])

  // The lightbox cycles through the top-level journey grid, so if the deep-linked journey isn't in it
  // (a child/variant run isn't shown on the overview) fall back to the standalone detail page once the
  // list has loaded — better than a dead click on a journey the lightbox can't render.
  useEffect(() => {
    if (!lightboxParam || loading || journeyDataLoading || journeys.length === 0) return
    if (!journeys.some(j => j.id === lightboxParam)) {
      navigate(`/projects/${projectId}/journeys/${lightboxParam}`, { replace: true })
    }
  }, [journeyDataLoading, journeys, lightboxParam, loading, navigate, projectId])

  // Close the lightbox and drop the deep-link param so it stays in sync with the URL and the same
  // journey can be re-opened from the chat afterwards.
  const closeLightbox = useCallback(() => {
    setLightboxJourneyId(null)
    setSearchParams(prev => {
      if (!prev.has('journey')) return prev
      const next = new URLSearchParams(prev)
      next.delete('journey')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const pause = projectPause(project)

  useEffect(() => () => {
    document.title = 'Replay QA'
  }, [])

  useEffect(() => {
    if (project) document.title = `Replay QA: ${project.name}`
  }, [project])

  // Close the lightbox when switching projects so a stale selection never lingers. Guarded on an
  // actual change (not the initial mount) so it doesn't clobber a `?journey=` deep-link opened on the
  // first render.
  const prevProjectIdRef = useRef(projectId)
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId
      setLightboxJourneyId(null)
    }
  }, [projectId])

  if (!projectId) return <Navigate to="/projects" replace />

  // Render nothing for the moment it takes the sign-in redirect to leave the page.
  if (redirectingToLogin) return null

  if (!loading && error) {
    return (
      <div className="project-overview__error" role="alert" data-testid="project-error">
        <p>{error ?? 'Project not found'}</p>
        <div className="project-overview__error-actions">
          {!user && error?.includes('Login required') && (
            <button type="button" onClick={() => login(dispatch, postLoginReturnTo(window.location.pathname, window.location.search))} data-testid="login-btn" data-analytics-event="new-overview-error-signin">
              <LogIn size={14} aria-hidden /> Sign in
            </button>
          )}
          {!accessDenied && (
            <button type="button" onClick={() => void projectQuery.refetch()} data-testid="retry-btn" data-analytics-event="new-overview-error-retry">
              <RefreshCw size={14} aria-hidden /> Retry
            </button>
          )}
          <button type="button" onClick={() => navigate('/')} data-analytics-event="new-overview-error-back">
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  const openBugCount = bugOpen
  const tabs = projectTabs({
    siteMapEnabled,
    playwrightEnabled,
    openBugCount,
  })
  // The settled tab list is only known once every query that can add or remove a tab has first
  // landed: the user settings (site_map + playwright_tests flags, via the hooks' `loaded`), the
  // project (Security pass), and the session probe (User Sessions). Until then the bar keeps its
  // reserved space but stays invisible — a tab that joins an already-painted flex row shoves every
  // rendered tab sideways (CLS on the overview header, issue #4027). These are first-load flags
  // only, so background polls refetching with cached data never re-hide the bar.
  const tabsSettled = siteMapLoaded && playwrightLoaded && !projectQuery.isLoading
  const bugCountsLoading = bugCountPending
  const bugTableLoading = overviewBugsPending
  const bugChartLoading = bugBreakdownPending
  const testRunDataLoading = testRunsLoading
  const showJourneysPreview = !testRunDataLoading && testRuns.length === 0
  const journeysPreviewLoading = testRunDataLoading || (showJourneysPreview && journeyDataLoading)

  return (
    <div
      className={`project-overview${showInitialTestRun ? ' project-overview--interim' : ''}`}
      data-testid="project-overview-page"
    >
      {/* Sticky tab bar. The project name, URL, and project actions live in the app top bar. During
          the initial-test-run interim the tabs are omitted so the full-screen setup view stands alone. */}
      {!showInitialTestRun && (
        <header
          className={`project-overview__header${tabsSettled ? '' : ' project-overview__header--tabs-pending'}`}
        >
          <TabNavigation tabs={tabs} activeTab={activeTab} basePath={`/projects/${projectId}`} />
        </header>
      )}

      {user && activeTab === 'overview' && <TestingSetupActions key={`banner-${projectId}`} projectId={projectId} bannerOnly />}

      {project && showInitialTestRun && (
        <InitialTestRunView
          projectId={projectId}
          targetUrl={initialTestRun.targetUrl}
          projectName={initialTestRun.projectName}
          explorationId={initialTestRun.explorationId}
          currentTask={initialTestRun.currentTask}
          journeyCount={initialTestRun.journeyCount}
          explorationCount={initialTestRun.explorationCount}
        />
      )}


      {showInitialTestRun && <PlaywrightInfoCard projectId={projectId} />}

      {!showInitialTestRun && activeTab === 'bugs' && (
        <BugListTab projectId={projectId} project={project} />
      )}

      {!showInitialTestRun && activeTab === 'test-runs' && (
        <TestRunListTab projectId={projectId} pause={pause} managed />
      )}

      {activeTab === 'site-map' && <SiteMapTab key={projectId} projectId={projectId!} />}
      {activeTab === 'playwright-tests' && <PlaywrightTestsTab key={projectId} projectId={projectId} />}

      {!showInitialTestRun && activeTab === 'security' && (
        <SecurityTab projectId={projectId} project={project} />
      )}

      {user && activeTab === 'overview' && <TestingSetupActions key={projectId} projectId={projectId} />}

      {!showInitialTestRun && activeTab === 'overview' && (
        <>
          <OverviewTestingSummary showSetup={false} projectId={projectId} runs={testRuns} runsLoading={testRunsLoading} />
          <PlaywrightInfoCard projectId={projectId} />

          {/* Active / most recent exploration replay, with prev/next to cycle through all explorations. */}
          <OverviewExplorations projectId={projectId} />

          {/* Unresolved bugs — a "Bugs <count>" header + a compact top-open-bugs table on the
              left, with the severity breakdown chart on the right (stacks on narrow widths). The
              full, filterable table lives on the Bugs tab. */}
          {(bugCountsLoading || openBugCount > 0) && (
            <section className="project-overview__bugs" data-testid="overview-bugs">
              <div className="project-overview__bugs-main">
                {bugCountsLoading ? (
                  <span className="project-overview__skeleton-line project-overview__skeleton-line--title" />
                ) : (
                  <UnresolvedBugsHeader count={openBugCount} />
                )}
                {bugTableLoading || bugCountsLoading ? <OverviewBugsSkeleton /> : <OverviewBugTable bugs={overviewBugs} projectId={projectId} />}
              </div>
              <div className="project-overview__bugs-chart">
                {bugChartLoading || bugCountsLoading ? <OverviewBugsSkeleton chart /> : (
                  <ChunkErrorBoundary eventTag="bug-breakdown-chart" fallback={<OverviewBugsSkeleton chart />}>
                    <Suspense fallback={<OverviewBugsSkeleton chart />}>
                      <BugBreakdownChart breakdown={bugBreakdownQuery.data ?? []} resolvedCount={bugCounts?.resolvedCount ?? 0} loading={bugBreakdownQuery.isLoading} />
                    </Suspense>
                  </ChunkErrorBoundary>
                )}
              </div>
            </section>
          )}

          {/* Journeys preview — shown only for a project with no test runs yet. Once runs exist the
              journeys live inside the run that executed them (see the run detail page), so this
              standalone list would just duplicate them. */}
          {/* The 5 most recent journeys (open-bug journeys first). Each row opens the journey
              lightbox. While the first load is in flight it renders a same-shape skeleton so the
              section reserves its space instead of pushing the explorations component down. */}
          {journeysPreviewLoading ? (
            <section
              className="project-overview__journeys project-overview__journeys--card"
              data-testid="overview-recent-journeys-loading"
              aria-busy="true"
            >
              <div className="project-overview__journeys-head">
                <h2 className="project-overview__journeys-title">Journeys</h2>
              </div>
              <ul className="project-overview__journey-list" aria-hidden>
                {Array.from({ length: 5 }).map((_, i) => (
                  <li key={i} className="project-overview__journey-skeleton" />
                ))}
              </ul>
            </section>
          ) : showJourneysPreview && journeys.length > 0 ? (
            <section className="project-overview__journeys project-overview__journeys--card" data-testid="overview-recent-journeys">
              <div className="project-overview__journeys-head">
                <h2 className="project-overview__journeys-title">Journeys</h2>
              </div>
              <ul className="project-overview__journey-list" data-testid="overview-recent-journeys-list">
                {recentJourneys.map(journey => (
                  <OverviewJourneyRow key={journey.id} journey={journey} onOpen={setLightboxJourneyId} pause={pause} />
                ))}
              </ul>
            </section>
          ) : null}

          {/* Test Runs — the most recent runs, with a search box and "View all →" into the tab. */}
          {testRunDataLoading
            ? <OverviewRunsSkeleton />
            : <OverviewTestRuns projectId={projectId} runs={testRuns} />}
        </>
      )}

      {/* Journey lightbox opened from the temporary overview preview. */}
      {!showInitialTestRun && lightboxIndex >= 0 && (
        <JourneyLightbox
          journeys={journeys}
          index={lightboxIndex}
          projectId={projectId}
          onClose={closeLightbox}
          onNavigate={i => setLightboxJourneyId(journeys[i]?.id ?? null)}
        />
      )}
    </div>
  )
}
